import type { ReactNode } from "react";
import { AlertTriangle, CalendarClock, Clock, Hourglass } from "lucide-react";
import { StatCard } from "../../patterns/StatCard";
import type { SummaryCountsItem } from "@shared-core/data/api-types";

/** Stable identity for each tile, independent of what clicking it does —
 *  `CsrHome` maps a tile key to its `PortfolioBoard` lane (scroll + expand);
 *  `CsrHomeAlt` maps it to a jobs-page filter link instead. Keeping this
 *  type here (rather than reusing `PortfolioBoardGroup`) is what lets a
 *  second page reuse this component without depending on board-lane shape. */
export type CommandCentreStatTileKey =
  "overdue" | "due_today" | "due_soon" | "not_production_ready";

interface StatTile {
  key: CommandCentreStatTileKey;
  label: string;
  value: number;
  icon: ReactNode;
}

// Every tile shares this min-height so a wrapping label ("Not production
// ready" is the one that wraps to two lines at these column widths) never
// makes its tile taller than its neighbours — all four sit on the same
// footprint regardless of label length. Card's own `gap-6` (meant for
// bigger card layouts) is overridden down to `gap-2`, which is what makes a
// two-line label fit inside a height a one-line tile doesn't look sparse in.
const _TILE_CLASSNAME = "h-full min-h-[4.5rem] gap-2";

/**
 * CSR home header stat strip — the four headline job counts from the
 * composed `CsrHomeDashboard.summary_counts` that don't already have their
 * own affordance elsewhere on the page: unread emails and active tickets
 * were dropped (the rail beside this strip already lists both, with
 * subjects and due dates — the bare counts were redundant), and At risk was
 * dropped too (alert-derived, and every board card already carries its own
 * badge for it).
 *
 * Every tile is a clickable filter: clicking calls `onSelectTile` with the
 * tile's own key — what that does is the caller's call (see
 * `CommandCentreStatTileKey`'s docstring).
 */
export function CommandCentreStatStrip({
  counts,
  onSelectTile,
}: {
  counts: SummaryCountsItem;
  onSelectTile: (tile: CommandCentreStatTileKey) => void;
}) {
  const tiles: StatTile[] = [
    {
      key: "overdue",
      label: "Overdue",
      value: counts.overdue_count,
      icon: <AlertTriangle className="size-4 text-red-600" />,
    },
    {
      key: "due_today",
      label: "Due today",
      value: counts.due_today_count,
      icon: <Clock className="size-4 text-amber-600" />,
    },
    {
      key: "due_soon",
      label: "Due soon",
      value: counts.due_soon_count,
      icon: <CalendarClock className="size-4 text-muted-foreground" />,
    },
    {
      key: "not_production_ready",
      label: "Not production ready",
      value: counts.not_production_ready_count,
      icon: <Hourglass className="size-4 text-muted-foreground" />,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {tiles.map((tile) => (
        <StatCard
          key={tile.key}
          label={tile.label}
          value={tile.value}
          icon={tile.icon}
          onClick={() => onSelectTile(tile.key)}
          className={_TILE_CLASSNAME}
        />
      ))}
    </div>
  );
}
